import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeftIcon, KeyIcon, CreditCardIcon, BuildingLibraryIcon, UserIcon } from '@heroicons/react/24/outline';
import AdminService from '../../services/admin.service';
import VaultSidebar from '../../components/vault/VaultSidebar';
import VaultHeader from '../../components/vault/VaultHeader';
import PasswordList from '../../components/vault/PasswordList';
import CardDetailsList from '../../components/cardDetails/CardDetailsList';
import CardDetailsModal from '../../components/cardDetails/CardDetailsModal';
import BankDetailsList from '../../components/bankDetails/BankDetailsList';
import PersonalDetailsList from '../../components/personalDetails/PersonalDetailsList';
import { IDecryptedPasswordEntry } from '../../types/vault.types';
import { CardDetail } from '../../types/cardDetails';
import { BankDetail } from '../../types/bankDetails';
import { PersonalDetail } from '../../types/personalDetails';
import BankDetailsModal from '../../components/bankDetails/BankDetailsModal';
import PersonalDetailsModal from '../../components/personalDetails/PersonalDetailsModal';

type TabType = 'passwords' | 'cardDetails' | 'bankDetails' | 'personalDetails';

const AdminCategoryItemsPage: React.FC = () => {
    const { userId, categoryId } = useParams<{ userId: string; categoryId: string }>();
    const navigate = useNavigate();

    const [entries, setEntries] = useState<IDecryptedPasswordEntry[]>([]);
    const [cardDetails, setCardDetails] = useState<CardDetail[]>([]);
    const [showCardModal, setShowCardModal] = useState(false);
    const [editCardDetail, setEditCardDetail] = useState<CardDetail | null>(null);
    const [isBankModalOpen, setIsBankModalOpen] = useState(false);
    const [selectedBank, setSelectedBank] = useState<BankDetail | null>(null);
    const [personalDetails, setPersonalDetails] = useState<PersonalDetail[]>([]);
    const [showPersonalModal, setShowPersonalModal] = useState(false);
    const [editPersonalDetail, setEditPersonalDetail] = useState<PersonalDetail | null>(null);
    const [bankDetails, setBankDetails] = useState<BankDetail[]>([]);
    const [categoryName, setCategoryName] = useState('');
    const [vaultName, setVaultName] = useState('');
    const [activeTab, setActiveTab] = useState<TabType>('passwords');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [loadingPasswords, setLoadingPasswords] = useState(false);
    const [loadingCards, setLoadingCards] = useState(false);
    const [loadingBanks, setLoadingBanks] = useState(false);
    const [loadingPersonals, setLoadingPersonals] = useState(false);
    const [counts, setCounts] = useState({
        passwords: 0,
        cardDetails: 0,
        bankDetails: 0,
        personalDetails: 0
    });

    useEffect(() => {
        if (userId && categoryId) {
            loadCategoryItems();
        }
    }, [userId, categoryId]);

    const loadCategoryItems = async () => {
        try {
            setLoading(true);
            console.log('Loading category items for user:', userId, 'category:', categoryId);

            const data = await AdminService.getCategoryItems(userId!, categoryId!);

            setEntries(data.passwords);
            setCardDetails(data.cardDetails);
            setBankDetails(data.bankDetails);
            setPersonalDetails(data.personalDetails);
            setCategoryName(categoryId === 'uncategorized' ? 'Unassigned' : data.category?.name || 'Category');
            setVaultName(data.vault?.name || 'Vault');
            setCounts(data.counts);

            toast.success('Items loaded successfully');
        } catch (error: any) {
            console.error('Error loading category items:', error);
            const errorMessage = error.message || 'Failed to load category items';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab);
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'passwords':
                return (
                    <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                        <PasswordList
                            entries={entries}
                            loading={loadingPasswords}
                            viewMode="grid"
                            hideFavoriteIcon={true}
                            permissionLevel="read"
                            isAdminView={true}
                        />
                    </div>
                );
            case 'cardDetails':
                return (
                    <>
                        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                            <CardDetailsList
                                details={cardDetails}
                                onUpdate={() => {}}
                                onEdit={(detail) => {
                                    setEditCardDetail(detail);
                                    setShowCardModal(true);
                                }}
                                hideFavoriteIcon={true}
                                isAdminView={true}
                            />
                        </div>
                        <CardDetailsModal
                            isOpen={showCardModal}
                            onClose={() => setShowCardModal(false)}
                            onSuccess={() => setShowCardModal(false)}
                            card={editCardDetail || undefined}
                            isAdminView={true}
                        />
                    </>
                );
            case 'bankDetails':
                return (
                    <>
                        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                            <BankDetailsList
                                details={bankDetails}
                                onUpdate={() => {}}
                                onEdit={(detail) => {
                                    setSelectedBank(detail);
                                    setIsBankModalOpen(true);
                                }}
                                hideFavoriteIcon={true}
                                isAdminView={true}
                            />
                        </div>
                        <BankDetailsModal
                            isOpen={isBankModalOpen}
                            onClose={() => setIsBankModalOpen(false)}
                            onSuccess={() => setIsBankModalOpen(false)}
                            bank={selectedBank || undefined}
                            isAdminView={true}
                        />
                    </>
                );
            case 'personalDetails':
                return (
                    <>
                        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                            <PersonalDetailsList
                                details={personalDetails}
                                onUpdate={() => {}}
                                onEdit={(detail) => {
                                    setEditPersonalDetail(detail);
                                    setShowPersonalModal(true);
                                }}
                                hideFavoriteIcon={true}
                                isAdminView={true}
                            />
                        </div>
                        <PersonalDetailsModal
                            isOpen={showPersonalModal}
                            onClose={() => setShowPersonalModal(false)}
                            onSuccess={() => setShowPersonalModal(false)}
                            personal={editPersonalDetail || undefined}
                            isAdminView={true}
                        />
                    </>
                );
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto"></div>
                    <p className="mt-6 text-lg text-gray-700 font-medium">Loading and decrypting items...</p>
                    <p className="mt-2 text-sm text-gray-500">This may take a moment</p>
                </div>
            </div>
        );
    }

    // Show error state if loading failed
    if (error) {
        return (
            <div className="min-h-screen bg-gray-50">
                <div className="bg-white border-b border-gray-200 shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                        <button
                            onClick={() => navigate('/manage-members')}
                            className="flex items-center text-gray-600 hover:text-indigo-600 transition-colors mb-4 font-medium"
                        >
                            <ArrowLeftIcon className="h-5 w-5 mr-2" />
                            Back to Team Members
                        </button>
                        <h1 className="text-3xl font-bold text-gray-900">Error Loading User Items</h1>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                        <div className="flex items-center mb-4">
                            <svg className="w-8 h-8 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h2 className="text-xl font-semibold text-red-800">Failed to Load Items</h2>
                        </div>
                        <p className="text-red-700 mb-4">{error}</p>
                        <div className="flex space-x-4">
                            <button
                                onClick={() => {
                                    setError(null);
                                    setLoading(true);
                                    loadCategoryItems();
                                }}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                            >
                                Try Again
                            </button>
                            <button
                                onClick={() => navigate(`/admin/user/${userId}/categories`)}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                            >
                                Go Back
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen flex overflow-hidden bg-gray-100">
            <VaultSidebar />
            <div className="flex flex-col flex-1 overflow-hidden">
                <VaultHeader />
                <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
                    {/* Hero Banner Section - Similar to VaultPage */}
                    <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 overflow-hidden">
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 right-0 w-96 h-48 bg-white rounded-full -translate-y-24 translate-x-48"></div>
                            <div className="absolute bottom-0 left-0 w-72 h-36 bg-white rounded-full translate-y-18 -translate-x-36"></div>
                            <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-white rounded-full"></div>
                        </div>
                        
                        <div className="relative px-6 py-12">
                            <div className="max-w-7xl mx-auto">
                                {/* Back Button */}
                                <button
                                    onClick={() => navigate(`/admin/user/${userId}/categories`)}
                                    className="flex items-center text-white/90 hover:text-white transition-colors mb-6 font-medium"
                                >
                                    <ArrowLeftIcon className="h-5 w-5 mr-2" />
                                    Back to Vaults
                                </button>

                                {/* Header Content */}
                                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
                                    <div className="mb-6 lg:mb-0">
                                        <div className="flex items-center mb-4">
                                            <h1 className="text-4xl lg:text-5xl font-extrabold text-white">
                                                {categoryName}
                                            </h1>
                                        </div>
                                        <div className="flex items-center space-x-2 text-yellow-300 bg-yellow-900/30 px-4 py-2 rounded-lg border border-yellow-400/30">
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                            <span className="font-semibold">Super Admin View (Read-Only)</span>
                                        </div>
                                        <p className="text-white/80 mt-3 text-sm">
                                            All access is logged for security compliance.
                                        </p>
                                    </div>
                                </div>
                                
                                {/* Stats Cards */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-white">{counts.passwords}</div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">Passwords</div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-white">{counts.cardDetails}</div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">Cards</div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-white">{counts.bankDetails}</div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">Banks</div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-white">{counts.personalDetails}</div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">Personal</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Tab Navigation */}
                                <div className="flex items-center justify-center px-4 sm:px-0">
                                    <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-1 sm:p-2 border border-white/30 w-full sm:w-auto sm:max-w-none overflow-x-auto">
                                        <nav className="flex space-x-1 sm:space-x-2 min-w-max sm:min-w-0 sm:justify-center">
                                            <button
                                                onClick={() => handleTabChange('passwords')}
                                                className={`relative px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${
                                                    activeTab === 'passwords'
                                                        ? 'bg-white text-red-600 shadow-lg scale-105'
                                                        : 'text-white/80 hover:text-white hover:bg-white/20'
                                                }`}
                                            >
                                                <div className="flex items-center space-x-1 sm:space-x-2">
                                                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                    </svg>
                                                    <span className="hidden sm:inline">Passwords</span>
                                                    <span className="sm:hidden">Pass</span>
                                                </div>
                                                {activeTab === 'passwords' && (
                                                    <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2 h-2 sm:w-3 sm:h-3 bg-yellow-400 rounded-full border-1 sm:border-2 border-white"></div>
                                                )}
                                            </button>
                                            
                                            <button
                                                onClick={() => handleTabChange('cardDetails')}
                                                className={`relative px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${
                                                    activeTab === 'cardDetails'
                                                        ? 'bg-white text-red-600 shadow-lg scale-105'
                                                        : 'text-white/80 hover:text-white hover:bg-white/20'
                                                }`}
                                            >
                                                <div className="flex items-center space-x-1 sm:space-x-2">
                                                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                    </svg>
                                                    <span>Cards</span>
                                                </div>
                                                {activeTab === 'cardDetails' && (
                                                    <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2 h-2 sm:w-3 sm:h-3 bg-yellow-400 rounded-full border-1 sm:border-2 border-white"></div>
                                                )}
                                            </button>
                                            
                                            <button
                                                onClick={() => handleTabChange('bankDetails')}
                                                className={`relative px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${
                                                    activeTab === 'bankDetails'
                                                        ? 'bg-white text-red-600 shadow-lg scale-105'
                                                        : 'text-white/80 hover:text-white hover:bg-white/20'
                                                }`}
                                            >
                                                <div className="flex items-center space-x-1 sm:space-x-2">
                                                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                    </svg>
                                                    <span className="hidden sm:inline">Bank Accounts</span>
                                                    <span className="sm:hidden">Banks</span>
                                                </div>
                                                {activeTab === 'bankDetails' && (
                                                    <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2 h-2 sm:w-3 sm:h-3 bg-yellow-400 rounded-full border-1 sm:border-2 border-white"></div>
                                                )}
                                            </button>
                                            
                                            <button
                                                onClick={() => handleTabChange('personalDetails')}
                                                className={`relative px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${
                                                    activeTab === 'personalDetails'
                                                        ? 'bg-white text-red-600 shadow-lg scale-105'
                                                        : 'text-white/80 hover:text-white hover:bg-white/20'
                                                }`}
                                            >
                                                <div className="flex items-center space-x-1 sm:space-x-2">
                                                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                    </svg>
                                                    <span className="hidden sm:inline">Personal Info</span>
                                                    <span className="sm:hidden">Personal</span>
                                                </div>
                                                {activeTab === 'personalDetails' && (
                                                    <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2 h-2 sm:w-3 sm:h-3 bg-yellow-400 rounded-full border-1 sm:border-2 border-white"></div>
                                                )}
                                            </button>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content Section */}
                    <div className="container mx-auto px-6 py-8">
                        <div className="space-y-6">{renderTabContent()}</div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AdminCategoryItemsPage;
